/**
 * ResponseTrendChart — Multi-line response time trend (one line per area type, 4 weeks).
 * Each area record has: area_type_name, week1, week2, week3, week4
 *
 * Grandstream campuses get a Response Time | Time to Connect toggle
 * (docs/TIME_TO_CONNECT_PLAN.md, phase 3). Time to Connect reads ttc_week1..4, the same
 * weekly buckets and area-type rows from sp_getAreaTypeSummary v2. A week with no intercom
 * alert is null, so the line leaves a gap there (operations: N/A, never zero). The series
 * order, and so every line's colour, is the same in both modes.
 */

import { useMemo, useState } from 'react';
import KpiToggle from '../../../../components/v2/KpiToggle.jsx';
import { TIME_TO_CONNECT_INFO } from '../../../reports/alert-details/timeToConnect.js';
import LineTrendingChart from '../../../../components/charts/LineTrendingChart.jsx';
import { useChartSeries } from '../../../../components/charts/chartPalette.js';
import { dashboardWeekRanges } from '../weekRanges.js';
import HeaderInfoTip from '../../../../components/v2/HeaderInfoTip.jsx';

const TREND_TOOLTIP = [
    'Displays the average response time in minutes for each 7-day period',
    'over the previous 4 weeks, broken down by care type. Click a care type',
    'in the legend to show or hide it on the graph.',
];

const METRICS = [
    { key: 'response', label: 'Response Time' },
    { key: 'connect', label: 'Time to Connect' },
];

/* ── TrendInfoTip — ⓘ icon with hover popover ── */
const TrendInfoTip = ({ metric }) => (
    <HeaderInfoTip
        title="About This Chart"
        body={metric === 'connect'
            ? `Displays the average Time to Connect in minutes for each 7-day period over the previous 4 weeks, `
              + `broken down by care type. ${TIME_TO_CONNECT_INFO} A care type with no intercom calls that week `
              + `(MC has no speakers) shows no point.`
            : TREND_TOOLTIP.join(' ')}
    />
);

const ResponseTrendChart = ({ areaData, isGrandstream = false }) => {
    const series = useChartSeries();
    const [metricChoice, setMetric] = useState('response');
    // A non-Grandstream campus can't be left on Time to Connect after a campus switch.
    const metric = isGrandstream ? metricChoice : 'response';
    const chartProps = useMemo(() => {
        if (!Array.isArray(areaData) || areaData.length === 0) return null;

        // Week labels, oldest to newest to match the lines below (week4, week3, week2, week1).
        // Shared with the caregiver table so both match the procs' day buckets.
        const labels = [...dashboardWeekRanges()].reverse();

        // Each area type becomes a line: [week4, week3, week2, week1]. `?? null` on the
        // connect series so a missing key is a gap too, never a zero.
        const lineData = metric === 'connect'
            ? areaData.map((at) => [at.ttc_week4 ?? null, at.ttc_week3 ?? null, at.ttc_week2 ?? null, at.ttc_week1 ?? null])
            : areaData.map((at) => [at.week4, at.week3, at.week2, at.week1]);
        const hasPoints = lineData.some((line) => line.some((v) => v != null));
        const lineLabels = areaData.map((at) => at.area_type_name || '');
        const lineColors = areaData.map((_, i) => series[i % series.length]);

        return { labels, lineData, lineLabels, lineColors, lines: areaData.length, hasPoints };
    }, [areaData, series, metric]);

    const title = metric === 'connect' ? 'Time to Connect Trends' : 'Response Time Trends';
    const toggle = isGrandstream
        ? <KpiToggle options={METRICS} value={metric} onChange={setMetric} label="Trend metric" />
        : null;

    if (!chartProps) {
        return (
            <div className="md-section">
                <div className="md-section__header">
                    <h3 className="md-section__title">{title}<TrendInfoTip metric={metric} /></h3>
                    {toggle}
                </div>
                <div style={{ textAlign: 'center', padding: 'var(--space-6)', color: 'var(--color-text-muted)' }}>No trend data available</div>
            </div>
        );
    }

    return (
        <div className="md-section">
            <div className="md-section__header">
                <h3 className="md-section__title">{title}<TrendInfoTip metric={metric} /></h3>
                {toggle}
            </div>
            {metric === 'connect' && !chartProps.hasPoints ? (
                <div style={{ textAlign: 'center', padding: 'var(--space-6)', color: 'var(--color-text-muted)' }}>
                    No intercom calls in the last 4 weeks
                </div>
            ) : (
            <div className="md-chart-container">
                <LineTrendingChart
                    style={{ maxHeight: '350px' }}
                    x_axis={true}
                    x_axis_title="Weeks"
                    line_axis={true}
                    line_axis_title={metric === 'connect' ? 'Time to Connect (min)' : 'Response Time (min)'}
                    lines={chartProps.lines}
                    line_color={chartProps.lineColors}
                    line_labels={chartProps.lineLabels}
                    line_data={chartProps.lineData}
                    forceAspect={true}
                    labels={chartProps.labels}
                />
            </div>
            )}
        </div>
    );
};

export default ResponseTrendChart;
